<template>
  <div class="column content-box">
    <div class="table-actions-wrap">
      <search :search-query="searchQuery" :on-input-search="onInputSearch" @on-search-submit="getWebhooks" />

      <div class="right-aligned-wrap">
        <woot-button color-scheme="success" icon="arrow-clockwise" @click="openAddPopup">
          {{ $t('WEBHOOKS_SEND_MESSAGES.HEADER_BUTTON_TEXT') }}
        </woot-button>
      </div>
    </div>

    <div class="medium-12">
      <table-content
        :items="webhooks"
        :show-search-empty-state="showEmptySearchResult"
        :is-loading="uiFlags.isFetching"
        @on-sort-change="onSortChange"
        @on-delete-click="openDeletePopup"
        @on-switch-change="onSwitchChange"
        @on-edit-click="openEditPopup"
      />

      <table-footer
        :current-page="Number(meta.current_page)"
        :total-count="meta.count"
        :page-size="15"
        @page-change="onPageChange"
      />
    </div>

    <woot-modal :show.sync="showAddPopup" :on-close="hideAddPopup" size="low">
      <Create @on-created="getWebhooks()" @on-close="hideAddPopup()"></Create>
    </woot-modal>
    <woot-modal :show.sync="showEditPopup" :on-close="hideEditPopup">
      <Edit
          v-if="showEditPopup"          
          :value="selected"
           @on-close="hideEditPopup()"
          @on-updated="getWebhooks()"
        />
    </woot-modal>

    <woot-delete-modal
      :show.sync="showDeleteConfirmationPopup"
      :on-close="closeDeletePopup"
      :on-confirm="confirmDeletion"
      :title="$t('WEBHOOKS_SEND_MESSAGES.DELETE.CONFIRM.TITLE')"
      :message="$t('WEBHOOKS_SEND_MESSAGES.DELETE.CONFIRM.MESSAGE')"
      :confirm-text="$t('WEBHOOKS_SEND_MESSAGES.DELETE.CONFIRM.YES')"
      :reject-text="$t('WEBHOOKS_SEND_MESSAGES.DELETE.CONFIRM.NO')"
      :is-loading="uiFlags.isDeleting"
    />
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import alertMixin from 'shared/mixins/alertMixin';
import Search from './Search';
import TableContent from './List';
import TableFooter from 'dashboard/components/widgets/TableFooter';
import Create from './Create.vue';
import Edit from './Edit.vue';

export default {
  data() {
    return {
      page: 1,
      searchQuery: '',
      oldSearchQuery: '',
      sortConfig: { name: 'desc' },
      showAddPopup: false,
      selected: {},
      showDeleteConfirmationPopup: false,
      showEditPopup: false      
    };
  },
  mixins: [alertMixin],
  components: {
    Search,
    TableContent,
    TableFooter,
    Create,
    Edit
  },
  computed: {
    ...mapGetters({
      meta: 'webhooksSendMessages/getMeta',
      webhooks: 'webhooksSendMessages/getWebhooksSendMessages',
      uiFlags: 'webhooksSendMessages/getUIFlags',
      inboxes: 'inboxes/getWhatsAppInboxes'
    }),    
    showEmptySearchResult() {
      return !!this.searchQuery && this.webhooks.length === 0;
    },
  },
  methods: {
    ...mapActions('webhooksSendMessages', ['get', 'search', 'delete', 'update']),
    async getWebhooks() {
      try {
        let value = '';

        if (this.searchQuery.charAt(0) === '+') {
          value = this.searchQuery.substring(1);
        } else {
          value = this.searchQuery;
        }

        const requestParams = {
          page: this.page,
          sortAttr: this.getSortAttribute(),
        };

        if (value) {
          if (this.oldSearchQuery && this.searchQuery !== this.oldSearchQuery) {
            this.page = 1;
            requestParams.page = this.page;
          }

          this.oldSearchQuery = this.searchQuery;
          requestParams.search = encodeURIComponent(value.toLowerCase());
          await this.search(requestParams);
        } else {
          await this.get(requestParams);
        }
      } catch (error) {
        this.showAlert(error.message);
      }
    },
    getSortAttribute() {
      let sortAttr = Object.keys(this.sortConfig).reduce((acc, sortKey) => {
        const sortOrder = this.sortConfig[sortKey];
        if (sortOrder) {
          const sortOrderSign = sortOrder === 'asc' ? '' : '-';
          return `${sortOrderSign}${sortKey}`;
        }
        return acc;
      }, '');

      if (!sortAttr) {
        this.sortConfig = { name: 'desc' };
        sortAttr = '-name';
      }

      return sortAttr;
    },
    async deleteWebhook(id) {
      try {
        await this.delete(id);

        this.showAlert(this.$t('WEBHOOKS_SEND_MESSAGES.DELETE.API.SUCCESS_MESSAGE'));

        this.clearData();
        this.getWebhooks();
      } catch (error) {
        this.showAlert(this.$t('WEBHOOKS_SEND_MESSAGES.DELETE.API.ERROR_MESSAGE'));
      } finally {
        this.closeDeletePopup();
      }
    },
    async updateWebhook({ id, data }) {
      try {
        await this.update({ id, data });

        if (data.enabled) {
          this.showAlert(this.$t('WEBHOOKS_SEND_MESSAGES.UPDATE.API.ENABLED_SUCCESS_MESSAGE'));
        } else {
          this.showAlert(this.$t('WEBHOOKS_SEND_MESSAGES.UPDATE.API.DISABLED_SUCCESS_MESSAGE'));
        }
        this.getWebhooks();

      } catch (error) {
        this.showAlert(this.$t('WEBHOOKS_SEND_MESSAGES.UPDATE.API.ERROR_MESSAGE'));
      }
    },
    onCreateWebhook() {
      this.clearData();
      this.getWebhooks();
    },
    onPageChange(page) {
      this.page = Number(page);

      this.getWebhooks();
    },
    onInputSearch(event) {
      const newQuery = event.target.value;
      const refetchAllCadences = !!this.searchQuery && newQuery === '';
      this.searchQuery = newQuery;

      if (refetchAllCadences) {
        this.getWebhooks();
      }
    },
    onSortChange(params) {
      this.sortConfig = params;

      this.page = 1;
      this.getWebhooks();
    },
    onSwitchChange(row, value) {
      this.updateWebhook({ id: row.id, data: { enabled: row.enabled } });
    },
    openAddPopup() {
      this.showAddPopup = true;
    },
    hideAddPopup() {
      this.showAddPopup = false;
    },
    openDeletePopup(response) {
      this.showDeleteConfirmationPopup = true;
      this.selected = response;
    },
    closeDeletePopup() {
      this.showDeleteConfirmationPopup = false;
    },
    confirmDeletion() {
      const { id } = this.selected;

      this.deleteWebhook(id);
    },
    clearData() {
      this.page = 1;
      this.searchQuery = '';
      this.oldSearchQuery = '';
      this.sortConfig = 'id';
    },
    openEditPopup(webhook) {
      this.showEditPopup = true;
      this.selected = webhook;
    },
    hideEditPopup() {
      this.showEditPopup = false;
    },
  },
  mounted() {
    this.getWebhooks();    
  },
};
</script>

<style lang="scss" scoped>
::v-deep {
  .modal-container {
    .columns.wait-message {
      p {
        margin-top: var(--space-large);
        margin-bottom: 0 !important;
      }

      ~ .modal-footer {
        button {
          opacity: 0.5;
          pointer-events: none;
        }
      }
    }
  }
}

.table-actions-wrap {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
</style>